import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Activity,
  ArrowRight,
  CheckCircle,
  FileCode2,
  Globe,
  List,
  Play,
  Plus,
  Radio,
  RefreshCw,
  RotateCcw,
  Server,
  ShieldCheck,
  Trash2,
  Wand2,
  Wifi,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import i18n from '../../lib/i18n';
import {
  Button,
  LifecycleControls,
  PageHeader,
  Select,
} from '../../components/ui';

// ── Types ────────────────────────────────────────────────────────────────────

type RealmMapping = { name: string; backend_id: string };
type NpsBackend = { id: string; label: string; type: 'nps'; host: string; port: number; secret: string };
type KcBackend = {
  id: string;
  label: string;
  type: 'kc';
  url: string;
  realm: string;
  client_id: string;
  client_secret?: string;
};
type BackendProfile = NpsBackend | KcBackend;

type EduroamStatus = {
  ok: boolean;
  module: string;
  realms: RealmMapping[];
  backends: BackendProfile[];
  configured: boolean;
  validation: { ok: boolean; errors: string[] };
  state: {
    applied_at: string | null;
    apply_count: number;
    last_error: string | null;
    generated_files?: string[];
    last_test?: Record<string, unknown> | null;
    container_name?: string | null;
  };
  certificates: Array<{ name: string; path: string }>;
  runtime: { container: string | null; running: boolean; deployment: string | null };
};

type EduroamConfig = {
  // Per-realm middleware: každý realm ukazuje na svůj backend profil (NPS
  // nebo KC) místo jednoho globálního přepínače pro celý node.
  realms: RealmMapping[];
  backends: BackendProfile[];
  // Servery federace jsou editovatelný seznam, ne pevný počet — federace
  // (CESnet) je může kdykoli přidat/odebrat/přejmenovat.
  national_proxy: { servers: Array<{ host: string; ip: string }>; port: number; secret: string };
  monitoring: { host: string; ip: string; secret: string };
  certificate: { cert_file: string; key_file: string; ca_file: string };
  test_user: { username: string; password: string; enabled: boolean };
  vlan_rules: Array<{ id: string; match: string; value: string; vlan: number; label: string }>;
  operator_name: string;
  logging: { module: string; enabled: boolean };
};

type EduroamEvent = { type: string; created_at: string; detail?: Record<string, unknown> };

type AuthEvent = {
  username: string;
  realm: string;
  result: 'accept' | 'reject' | 'proxied';
  source_ip?: string;
  timestamp?: string;
  is_cesnet_probe?: boolean;
  is_test_account?: boolean;
};

type ForwardedRealm = { realm: string; count: number; last_seen: string };

type FlrStatus = {
  host: string;
  ip: string;
  reachable: boolean;
  ping_ms?: number;
  radsec_ok?: boolean;
  checked_at: string;
};

// ── Default form state ────────────────────────────────────────────────────────

const DEFAULT_BACKEND_ID = 'default-nps';

const defaultForm: EduroamConfig = {
  realms: [],
  backends: [
    { id: DEFAULT_BACKEND_ID, label: i18n.t('eduroam.defaultNps', 'Výchozí NPS'), type: 'nps', host: '', port: 1812, secret: '' },
  ],
  national_proxy: {
    servers: [
      { host: 'flr1.eduroam.cz', ip: '78.128.248.10' },
      { host: 'flr2.eduroam.cz', ip: '78.128.248.11' },
      { host: 'flr3.eduroam.cz', ip: '78.128.248.12' },
    ],
    port: 2083,
    secret: 'radsec',
  },
  monitoring: { host: 'monitoring.eduroam.cz', ip: '78.128.248.234', secret: '' },
  certificate: {
    cert_file: '/etc/freeradius/certs/eduroam/server.crt',
    key_file: '/etc/freeradius/certs/eduroam/server.key',
    ca_file: '/etc/freeradius/certs/eduroam/ca.crt',
  },
  test_user: { username: 'test', password: '', enabled: true },
  vlan_rules: [],
  operator_name: '',
  logging: { module: 'logy', enabled: true },
};

// ── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'status', label: 'eduroam.tabStatus', icon: Activity },
  { id: 'realms', label: 'eduroam.tabRealms', icon: Globe },
  { id: 'monitor', label: 'eduroam.tabMonitor', icon: Radio },
  { id: 'config', label: 'eduroam.tabConfig', icon: Server },
  { id: 'certs', label: 'eduroam.tabCerts', icon: FileCode2 },
  { id: 'vlan', label: 'eduroam.tabVlan', icon: List },
  { id: 'events', label: 'eduroam.tabEvents', icon: ShieldCheck },
] as const;

type TabId = typeof TABS[number]['id'];

// ── Main component ────────────────────────────────────────────────────────────

export default function EduroamModule({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabId>('status');

  const statusQ = useQuery<EduroamStatus>({
    queryKey: ['eduroam-status'],
    queryFn: () => api.mcp('sso.eduroam_status', {}),
    refetchInterval: 30_000,
  });

  const configQ = useQuery<EduroamConfig>({
    queryKey: ['eduroam-config'],
    queryFn: async () => {
      const res = await api.mcp<{ config: EduroamConfig }>('sso.eduroam_config', { action: 'get' });
      return (res as { config: EduroamConfig }).config ?? res;
    },
  });

  const eventsQ = useQuery<EduroamEvent[]>({
    queryKey: ['eduroam-events'],
    queryFn: async () => {
      const res = await api.mcp<{ events: EduroamEvent[] }>('sso.eduroam_logs', {});
      return res.events ?? [];
    },
    refetchInterval: 30_000,
  });

  const authLogQ = useQuery<AuthEvent[]>({
    queryKey: ['eduroam-auth-log'],
    queryFn: async () => {
      const res = await api.mcp<{ events: AuthEvent[] }>('sso.eduroam_logs_auth', {});
      return res.events ?? [];
    },
    enabled: tab === 'monitor',
    refetchInterval: tab === 'monitor' ? 15_000 : false,
  });

  const forwardedQ = useQuery<ForwardedRealm[]>({
    queryKey: ['eduroam-forwarded'],
    queryFn: async () => {
      const res = await api.mcp<{ realms: ForwardedRealm[] }>('sso.eduroam_realms_forwarded', {});
      return res.realms ?? [];
    },
    enabled: tab === 'monitor',
    refetchInterval: tab === 'monitor' ? 30_000 : false,
  });

  const flrQ = useQuery<FlrStatus[]>({
    queryKey: ['eduroam-flr-status'],
    queryFn: async () => {
      const res = await api.mcp<{ servers: FlrStatus[] }>('sso.eduroam_flr_status', {});
      return res.servers ?? [];
    },
    enabled: tab === 'monitor',
    refetchInterval: tab === 'monitor' ? 60_000 : false,
    staleTime: 30_000,
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['eduroam-status'] });
    qc.invalidateQueries({ queryKey: ['eduroam-config'] });
    qc.invalidateQueries({ queryKey: ['eduroam-events'] });
    if (tab === 'monitor') {
      qc.invalidateQueries({ queryKey: ['eduroam-auth-log'] });
      qc.invalidateQueries({ queryKey: ['eduroam-forwarded'] });
    }
  };

  const applyMut = useMutation({
    mutationFn: () => api.mcp('sso.eduroam_apply', {}),
    onSuccess: (res: { ok: boolean; validation?: { errors?: string[] } }) => {
      if (!res.ok) toast.error(res.validation?.errors?.join(', ') || t('eduroam.applyFailed'));
      else toast.success(t('eduroam.configGenerated'));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const manageMut = useMutation({
    mutationFn: (action: 'start' | 'stop' | 'restart' | 'reload' | 'recreate') =>
      api.mcp('sso.eduroam_manage', { action }),
    onSuccess: (_r, action) => {
      toast.success(t('eduroam.runtimeAction', { action }));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const status = statusQ.data;
  const isRunning = status?.runtime.running ?? false;
  const isValid = status?.validation.ok ?? false;

  return (
    <div className='space-y-5'>
      {!embedded && (
        <PageHeader
          title='eduroam'
          description={t('eduroam.pageDescription')}
          icon={Wifi}
        />
      )}

      {/* Summary stats */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Stat
          label='Runtime'
          value={isRunning ? t('eduroam.running') : t('eduroam.stopped')}
          tone={isRunning ? 'green' : 'red'}
        />
        <Stat label={t('eduroam.validationLabel')} value={isValid ? 'OK' : t('eduroam.errorShort')} tone={isValid ? 'green' : 'red'} />
        <Stat label={t('eduroam.realms')} value={String(status?.realms?.length ?? 0)} />
        <Stat label={t('eduroam.certificates')} value={String(status?.certificates?.length ?? 0)} />
      </div>

      {/* Validation errors */}
      {status && !status.validation.ok && (
        <div className='rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300 space-y-1'>
          {status.validation.errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      {/* Tabs */}
      <div className='flex gap-1 flex-wrap border-b border-gray-800 pb-0'>
        {TABS.map((tb) => {
          const Icon = tb.icon;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-t-lg transition-colors border-b-2 -mb-px ${
                tab === tb.id
                  ? 'text-blue-400 border-blue-500 bg-blue-500/5'
                  : 'text-gray-500 border-transparent hover:text-white hover:bg-gray-800'
              }`}
            >
              <Icon size={13} />
              {t(tb.label)}
            </button>
          );
        })}
        <button
          onClick={refreshAll}
          className='ml-auto p-2 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg'
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Tab content */}
      {tab === 'status' && <StatusTab status={status} manageMut={manageMut} applyMut={applyMut} />}
      {tab === 'realms' && <RealmsTab configQ={configQ} refreshAll={refreshAll} />}
      {tab === 'monitor' && <MonitorTab authLogQ={authLogQ} forwardedQ={forwardedQ} flrQ={flrQ} />}
      {tab === 'config' && <ConfigTab configQ={configQ} refreshAll={refreshAll} />}
      {tab === 'certs' && <CertsTab status={status} refreshAll={refreshAll} />}
      {tab === 'vlan' && <VlanTab configQ={configQ} refreshAll={refreshAll} />}
      {tab === 'events' && <EventsTab events={eventsQ.data ?? []} />}
    </div>
  );
}

// ── Status tab ────────────────────────────────────────────────────────────────

function StatusTab({
  status,
  manageMut,
  applyMut,
}: {
  status?: EduroamStatus;
  manageMut: { mutate: (a: 'start' | 'stop' | 'restart') => void; isPending: boolean };
  applyMut: { mutate: () => void; isPending: boolean };
}) {
  const { t } = useTranslation();
  const state = status?.state;
  return (
    <div className='space-y-4'>
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
        <h2 className='text-white font-semibold text-sm'>Runtime</h2>
        <div className='grid grid-cols-2 gap-2 text-sm'>
          <InfoRow label={t('eduroam.container')} value={status?.runtime.container ?? '—'} />
          <InfoRow label='Deployment' value={status?.runtime.deployment ?? '—'} />
          <InfoRow
            label={t('eduroam.lastApply')}
            value={state?.applied_at ? new Date(state.applied_at).toLocaleString('cs-CZ') : t('eduroam.never')}
          />
          <InfoRow label={t('eduroam.applyCount')} value={String(state?.apply_count ?? 0)} />
          <InfoRow label={t('eduroam.errorLabel')} value={state?.last_error ?? t('eduroam.none')} />
          <InfoRow label={t('eduroam.generatedFiles')} value={String(state?.generated_files?.length ?? 0)} />
        </div>
        <div className='flex gap-2 flex-wrap pt-2'>
          <LifecycleControls.Bar
            name='eduroam'
            running={status?.runtime.running ?? false}
            loading={manageMut.isPending}
            onAction={(action) => manageMut.mutate(action)}
          />
          <Button
            variant='secondary'
            size='sm'
            icon={<RefreshCw size={11} />}
            disabled={manageMut.isPending}
            title={t('eduroam.reloadTitle')}
            onClick={() => manageMut.mutate('reload')}
          >
            Reload
          </Button>
          <Button
            variant='secondary'
            size='sm'
            icon={<RotateCcw size={11} />}
            disabled={manageMut.isPending}
            title={t('eduroam.recreateTitle')}
            onClick={() => manageMut.mutate('recreate')}
          >
            Recreate
          </Button>
        </div>

        <div className='mt-3 rounded-xl border border-gray-800 bg-gray-950/40 px-3 py-2 text-xs text-gray-500 space-y-0.5'>
          <div>
            <span className='text-gray-400'>Restart</span>{' '}
            {t('eduroam.restartHelp')}
          </div>
          <div>
            <span className='text-gray-400'>Reload</span>{' '}
            {t('eduroam.reloadHelp')}
          </div>
          <div>
            <span className='text-gray-400'>Recreate</span>{' '}
            {t('eduroam.recreateHelp')}
          </div>
        </div>

        <div className='flex flex-wrap gap-3 items-center pt-2'>
          <Button
            variant='secondary'
            size='sm'
            loading={applyMut.isPending}
            icon={<Play size={11} />}
            title={t('eduroam.applyTitle')}
            onClick={() => applyMut.mutate()}
          >
            {applyMut.isPending ? t('eduroam.generating') : 'Apply config'}
          </Button>
          <ValidateConfigButton />
          <span className='text-xs text-gray-600'>Apply → Validate → Restart/Reload</span>
        </div>
      </div>

      {/* Last test result */}
      {state?.last_test && (
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5'>
          <h2 className='text-white font-semibold text-sm mb-3'>{t('eduroam.lastAuthTest')}</h2>
          <pre className='text-xs text-gray-400 bg-gray-950/50 rounded-xl p-3 overflow-auto'>
            {JSON.stringify(state.last_test, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Realms tab ────────────────────────────────────────────────────────────────

function newBackendId(type: 'nps' | 'kc'): string {
  return `${type}-${Date.now().toString(36)}`;
}

function BackendEditor(
  { backend, onChange }: { backend: BackendProfile; onChange: (patch: Partial<BackendProfile>) => void },
) {
  const { t } = useTranslation();
  if (backend.type === 'nps') {
    return (
      <div className='grid grid-cols-2 gap-2 pt-2'>
        <Field label='Host'>
          <input
            className={inputCls}
            value={backend.host}
            onChange={(e) => onChange({ host: e.target.value })}
          />
        </Field>
        <Field label='Port'>
          <input
            className={inputCls}
            type='number'
            value={backend.port}
            onChange={(e) => onChange({ port: Number(e.target.value) })}
          />
        </Field>
        <Field label={t('eduroam.sharedSecret')}>
          <input
            className={inputCls}
            type='password'
            value={backend.secret}
            onChange={(e) => onChange({ secret: e.target.value })}
          />
        </Field>
      </div>
    );
  }
  return (
    <div className='grid grid-cols-2 gap-2 pt-2'>
      <Field label='KC URL'>
        <input className={inputCls} value={backend.url} onChange={(e) => onChange({ url: e.target.value })} />
      </Field>
      <Field label='Realm'>
        <input
          className={inputCls}
          value={backend.realm}
          onChange={(e) => onChange({ realm: e.target.value })}
        />
      </Field>
      <Field label='Client ID'>
        <input
          className={inputCls}
          value={backend.client_id}
          onChange={(e) => onChange({ client_id: e.target.value })}
        />
      </Field>
      <Field label={t('eduroam.clientSecretOptional')}>
        <input
          className={inputCls}
          type='password'
          value={backend.client_secret ?? ''}
          onChange={(e) => onChange({ client_secret: e.target.value })}
        />
      </Field>
    </div>
  );
}

function RealmsTab(
  { configQ, refreshAll }: { configQ: { data?: EduroamConfig }; refreshAll: () => void },
) {
  const { t } = useTranslation();
  const [newRealm, setNewRealm] = useState('');
  const [domainsInput, setDomainsInput] = useState('');
  const [generatedRegex, setGeneratedRegex] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const realms = configQ.data?.realms ?? [];
  const backends = configQ.data?.backends ?? [];

  const saveRealms = useMutation({
    mutationFn: (next: RealmMapping[]) => api.mcp('sso.eduroam_config', { action: 'set', patch: { realms: next } }),
    onSuccess: () => {
      toast.success(t('eduroam.realmsSaved'));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveBackends = useMutation({
    mutationFn: (next: BackendProfile[]) =>
      api.mcp('sso.eduroam_config', { action: 'set', patch: { backends: next } }),
    onSuccess: () => refreshAll(),
    onError: (e: Error) => toast.error(e.message),
  });

  const addRealm = () => {
    const r = newRealm.trim();
    if (!r || realms.some((x) => x.name === r)) return;
    saveRealms.mutate([...realms, { name: r, backend_id: backends[0]?.id ?? '' }]);
    setNewRealm('');
  };

  const removeRealm = (name: string) => {
    saveRealms.mutate(realms.filter((x) => x.name !== name));
  };

  const setRealmBackend = (name: string, backend_id: string) => {
    saveRealms.mutate(realms.map((x) => (x.name === name ? { ...x, backend_id } : x)));
  };

  const addBackend = (type: 'nps' | 'kc') => {
    const id = newBackendId(type);
    const backend: BackendProfile = type === 'nps'
      ? { id, label: t('eduroam.newNpsBackend'), type: 'nps', host: '', port: 1812, secret: '' }
      : {
        id,
        label: t('eduroam.newKcBackend'),
        type: 'kc',
        url: 'https://login.services.maxcloud.cz',
        realm: 'master',
        client_id: 'radius',
      };
    saveBackends.mutate([...backends, backend]);
  };

  const updateBackend = (id: string, patch: Partial<BackendProfile>) => {
    saveBackends.mutate(
      backends.map((b) => (b.id === id ? { ...b, ...patch } as BackendProfile : b)),
    );
  };

  const removeBackend = (id: string) => {
    const inUse = realms.filter((r) => r.backend_id === id);
    if (inUse.length > 0) {
      toast.error(t('eduroam.backendInUse', { realms: inUse.map((r) => r.name).join(', ') }));
      return;
    }
    saveBackends.mutate(backends.filter((b) => b.id !== id));
  };

  const generateRegex = async () => {
    const domains = domainsInput.split('\n').map((s) => s.trim()).filter(Boolean);
    if (!domains.length) return;
    setIsGenerating(true);
    try {
      const res = await api.mcp<{ regex: string }>('sso.eduroam_realms_regex', { domains });
      setGeneratedRegex(res.regex);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className='space-y-5'>
      {/* Backend profily — per-realm middleware: NPS (on-prem AD) nebo KC
          (přímo Keycloak) přednavolitelné, přiřazují se realmům níž. */}
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
        <div className='flex items-center justify-between'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.backendProfiles')}</h2>
          <div className='flex gap-2'>
            <button onClick={() => addBackend('nps')} className={secondaryBtn}>
              <Plus size={13} /> NPS
            </button>
            <button onClick={() => addBackend('kc')} className={secondaryBtn}>
              <Plus size={13} /> Keycloak
            </button>
          </div>
        </div>
        <p className='text-xs text-gray-500'>{t('eduroam.backendProfilesDesc')}</p>
        <div className='space-y-2'>
          {backends.map((b) => (
            <div key={b.id} className='rounded-xl border border-gray-800 bg-gray-950/50 p-3'>
              <div className='flex items-center gap-2'>
                <span
                  className={`text-[10px] shrink-0 border rounded px-1.5 py-0.5 ${
                    b.type === 'nps'
                      ? 'text-blue-400 border-blue-500/30'
                      : 'text-emerald-400 border-emerald-500/30'
                  }`}
                >
                  {b.type === 'nps' ? 'NPS' : 'Keycloak'}
                </span>
                <input
                  className={`${inputCls} flex-1`}
                  value={b.label}
                  onChange={(e) => updateBackend(b.id, { label: e.target.value })}
                />
                <button
                  onClick={() => removeBackend(b.id)}
                  className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 shrink-0'
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <BackendEditor backend={b} onChange={(patch) => updateBackend(b.id, patch)} />
            </div>
          ))}
          {backends.length === 0 && (
            <div className='text-xs text-gray-600 text-center py-4'>{t('eduroam.noBackends')}</div>
          )}
        </div>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>
        {/* Realm list */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-4'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.localRealms')}</h2>
          <p className='text-xs text-gray-500'>
            {t('eduroam.localRealmsDesc')}
          </p>
          <div className='space-y-2'>
            {realms.map((r) => (
              <div
                key={r.name}
                className='flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950/50 px-3 py-2'
              >
                <div className='flex items-center gap-2 min-w-0'>
                  <Globe size={13} className='text-blue-400 shrink-0' />
                  <span className='text-sm text-white font-mono truncate'>{r.name}</span>
                </div>
                <div className='flex items-center gap-2 shrink-0'>
                  <Select
                    value={r.backend_id}
                    onChange={(e) => setRealmBackend(r.name, e.target.value)}
                    options={backends.map((b) => ({ value: b.id, label: b.label }))}
                  />
                  <button
                    onClick={() => removeRealm(r.name)}
                    className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10'
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
            {realms.length === 0 && (
              <div className='text-xs text-gray-600 text-center py-4'>{t('eduroam.noRealm')}</div>
            )}
          </div>
          <div className='flex gap-2'>
            <input
              className={`${inputCls} flex-1`}
              value={newRealm}
              onChange={(e) => setNewRealm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRealm()}
              placeholder='school.example'
            />
            <button
              onClick={addRealm}
              disabled={!newRealm.trim() || saveRealms.isPending}
              className={primaryBtn}
            >
              <Plus size={13} /> {t('eduroam.add')}
            </button>
          </div>
        </div>

        {/* Regex generator */}
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-4'>
        <div className='flex items-center gap-2'>
          <Wand2 size={15} className='text-amber-400' />
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.regexGenerator')}</h2>
        </div>
        <p className='text-xs text-gray-500'>
          {t('eduroam.regexDesc1')}{' '}
          <code className='text-gray-300'>realm</code> {t('eduroam.regexDesc2')}
        </p>
        <Field label={t('eduroam.domains')}>
          <textarea
            className={`${inputCls} min-h-[100px] font-mono`}
            value={domainsInput}
            onChange={(e) => setDomainsInput(e.target.value)}
            placeholder={'school.example\nsub.school.example\nother.cz'}
          />
        </Field>
        <button
          onClick={generateRegex}
          disabled={isGenerating || !domainsInput.trim()}
          className={primaryBtn}
        >
          <Wand2 size={13} /> {isGenerating ? t('eduroam.generating') : t('eduroam.generateRegex')}
        </button>
        {generatedRegex && (
          <div className='space-y-1'>
            <div className='text-xs text-gray-500'>{t('eduroam.regexResult')}</div>
            <div
              className='font-mono text-sm text-emerald-300 bg-gray-950/60 rounded-xl px-3 py-2 border border-emerald-500/20 cursor-pointer select-all'
              onClick={() => {
                navigator.clipboard.writeText(generatedRegex);
                toast.success(t('eduroam.copied'));
              }}
              title={t('eduroam.clickToCopy')}
            >
              {generatedRegex}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ── Monitor tab ───────────────────────────────────────────────────────────────

function MonitorTab({
  authLogQ,
  forwardedQ,
  flrQ,
}: {
  authLogQ: { data?: AuthEvent[]; isLoading: boolean; refetch: () => void };
  forwardedQ: { data?: ForwardedRealm[]; isLoading: boolean };
  flrQ: { data?: FlrStatus[]; isLoading: boolean; refetch: () => void };
}) {
  const { t } = useTranslation();
  const events = authLogQ.data ?? [];
  const forwarded = forwardedQ.data ?? [];
  const flrServers = flrQ.data ?? [];

  const accepts = events.filter((e) => e.result === 'accept').length;
  const rejects = events.filter((e) => e.result === 'reject').length;
  const proxied = events.filter((e) => e.result === 'proxied').length;
  const probeHits = events.filter((e) => e.is_cesnet_probe).length;
  const testAccountEvents = events.filter((e) => e.is_test_account);
  const lastTestAccountSeen = testAccountEvents[0]?.timestamp;

  return (
    <div className='space-y-5'>
      {/* CESnet FLR server status */}
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-white font-semibold text-sm'>
              {t('eduroam.flrTitle')}
            </h2>
            <p className='text-xs text-gray-500 mt-0.5'>
              {t('eduroam.flrDesc')}
            </p>
          </div>
          <button
            onClick={() => flrQ.refetch()}
            className='p-1.5 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg'
          >
            <RefreshCw size={13} />
          </button>
        </div>
        {flrQ.isLoading
          ? (
            <div className='grid grid-cols-3 gap-3'>
              {[0, 1, 2].map((i) => (
                <div key={i} className='h-20 bg-gray-800/40 rounded-xl animate-pulse' />
              ))}
            </div>
          )
          : (
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
              {flrServers.map((srv) => <FlrServerCard key={srv.host} srv={srv} />)}
              {flrServers.length === 0 && (
                <div className='col-span-3 text-center py-4 text-gray-600 text-sm'>
                  {t('eduroam.clickRefresh')}
                </div>
              )}
            </div>
          )}
      </div>

      {/* Summary */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
        <Stat label='Accept' value={String(accepts)} tone='green' />
        <Stat label='Reject' value={String(rejects)} tone='red' />
        <Stat label='Proxied' value={String(proxied)} tone='blue' />
        <Stat
          label={t('eduroam.federationProbe')}
          value={String(probeHits)}
          tone={probeHits > 0 ? 'green' : 'blue'}
        />
        <Stat
          label={t('eduroam.testAccountActivity')}
          value={lastTestAccountSeen
            ? new Date(lastTestAccountSeen).toLocaleTimeString('cs-CZ')
            : t('eduroam.none')}
          tone={testAccountEvents.length > 0 ? 'green' : 'red'}
        />
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>
        {/* Auth log — s rozlišením CESnet probe */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-white font-semibold text-sm'>{t('eduroam.authEvents')}</h2>
              <p className='text-xs text-gray-500'>
                <span className='inline-block w-2 h-2 rounded-full bg-amber-400 mr-1' />
                {t('eduroam.yellowProbe')}
              </p>
            </div>
            <button
              onClick={() => authLogQ.refetch()}
              className='p-1.5 text-gray-600 hover:text-white hover:bg-gray-800 rounded-lg'
            >
              <RefreshCw size={13} />
            </button>
          </div>
          {authLogQ.isLoading
            ? (
              <div className='space-y-2'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-10 bg-gray-800/40 rounded-xl animate-pulse' />
                ))}
              </div>
            )
            : events.length === 0
            ? (
              <div className='text-center py-8 text-gray-600 text-sm'>
                {t('eduroam.noAuthEvents')}
              </div>
            )
            : (
              <div className='space-y-1.5 max-h-96 overflow-y-auto pr-1'>
                {events.slice(0, 100).map((ev, i) => <AuthEventRow key={i} ev={ev} />)}
              </div>
            )}
        </div>

        {/* Forwarded realms */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.forwardedRealms')}</h2>
          {forwardedQ.isLoading
            ? (
              <div className='space-y-2'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className='h-12 bg-gray-800/40 rounded-xl animate-pulse' />
                ))}
              </div>
            )
            : forwarded.length === 0
            ? <div className='text-center py-8 text-gray-600 text-sm'>{t('eduroam.noForwarded')}</div>
            : (
              <div className='space-y-2'>
                {forwarded.map((r) => (
                  <div
                    key={r.realm}
                    className='flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950/50 px-3 py-2'
                  >
                    <div>
                      <div className='text-sm text-white font-mono'>{r.realm}</div>
                      <div className='text-xs text-gray-500'>
                        {t('eduroam.lastLabel')}{' '}
                        {r.last_seen ? new Date(r.last_seen).toLocaleString('cs-CZ') : '—'}
                      </div>
                    </div>
                    <div className='flex items-center gap-1.5 text-blue-400'>
                      <ArrowRight size={13} />
                      <span className='text-sm font-semibold'>{r.count}×</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function FlrServerCard({ srv }: { srv: FlrStatus }) {
  const { t } = useTranslation();
  const allOk = srv.radsec_ok && srv.reachable;
  const border = allOk
    ? 'border-emerald-500/20 bg-emerald-500/5'
    : srv.reachable
    ? 'border-amber-500/20 bg-amber-500/5'
    : 'border-red-500/20 bg-red-500/5';

  return (
    <div className={`rounded-xl border ${border} p-3 space-y-2`}>
      <div className='flex items-center gap-2'>
        {allOk
          ? <CheckCircle size={13} className='text-emerald-400 shrink-0' />
          : srv.reachable
          ? <Activity size={13} className='text-amber-400 shrink-0' />
          : <XCircle size={13} className='text-red-400 shrink-0' />}
        <span className='text-sm text-white font-mono truncate'>{srv.host}</span>
      </div>
      <div className='text-xs text-gray-500 space-y-0.5'>
        <div className='flex justify-between'>
          <span>Ping</span>
          <span className={srv.ping_ms !== undefined ? 'text-emerald-400' : 'text-red-400'}>
            {srv.ping_ms !== undefined ? `${srv.ping_ms}ms` : 'timeout'}
          </span>
        </div>
        <div className='flex justify-between'>
          <span>RadSec TCP/2083</span>
          <span className={srv.radsec_ok ? 'text-emerald-400' : 'text-red-400'}>
            {srv.radsec_ok ? 'OK' : t('eduroam.errorLower')}
          </span>
        </div>
        <div className='text-gray-700 text-[10px] mt-1'>{srv.ip}</div>
      </div>
    </div>
  );
}

function AuthEventRow({ ev }: { ev: AuthEvent }) {
  const { t } = useTranslation();
  const icon = ev.result === 'accept'
    ? <CheckCircle size={13} className='text-emerald-400 flex-shrink-0' />
    : ev.result === 'reject'
    ? <XCircle size={13} className='text-red-400 flex-shrink-0' />
    : <ArrowRight size={13} className='text-blue-400 flex-shrink-0' />;

  const ts = ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString('cs-CZ') : '';
  const borderCls = ev.is_cesnet_probe
    ? 'border-amber-500/20 bg-amber-500/5'
    : 'border-gray-800 bg-gray-950/40';

  return (
    <div className={`flex items-center gap-2 rounded-lg border ${borderCls} px-2.5 py-1.5 text-xs`}>
      {icon}
      <span className='text-white font-mono truncate flex-1'>
        {ev.username}@{ev.realm}
      </span>
      {ev.is_test_account && (
        <span className='text-emerald-400 text-[10px] shrink-0 border border-emerald-500/30 rounded px-1'>
          {t('eduroam.testUcet', 'test účet')}
        </span>
      )}
      {ev.is_cesnet_probe && (
        <span className='text-amber-400 text-[10px] shrink-0 border border-amber-500/30 rounded px-1'>
          probe
        </span>
      )}
      {ev.source_ip && <span className='text-gray-600 shrink-0'>{ev.source_ip}</span>}
      {ts && <span className='text-gray-600 shrink-0'>{ts}</span>}
    </div>
  );
}

// ── Config tab ────────────────────────────────────────────────────────────────

function ConfigTab(
  { configQ, refreshAll }: { configQ: { data?: EduroamConfig }; refreshAll: () => void },
) {
  const { t } = useTranslation();
  const [form, setForm] = useState<EduroamConfig>(defaultForm);
  const formInitialized = useRef(false);

  useEffect(() => {
    if (!configQ.data || formInitialized.current) return;
    formInitialized.current = true;
    setForm(configQ.data);
  }, [configQ.data]);

  const saveMut = useMutation({
    mutationFn: (body: Partial<EduroamConfig>) => api.mcp('sso.eduroam_config', { action: 'set', patch: body }),
    onSuccess: () => {
      toast.success(t('eduroam.configSaved'));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (body: { username: string; realm?: string }) => api.mcp('sso.eduroam_test_auth', body),
    onSuccess: () => {
      toast.success(t('eduroam.dryRunDone'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [testRealm, setTestRealm] = useState('');
  const [testUser, setTestUser] = useState('test');

  const submitConfig = (e: FormEvent) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  return (
    <div className='space-y-5'>
      <form className='grid grid-cols-1 xl:grid-cols-2 gap-5' onSubmit={submitConfig}>
        {/* Backend profily (NPS/KC) se teď spravují per-realm v záložce
            "Realmy" (RealmsTab) — viz middleware refactor. Tady zůstává jen
            federace/monitoring/certifikáty, což je společné pro celý node. */}

        {/* National proxy — servery federace jsou editovatelný seznam (federace je
            může kdykoli změnit, nic tu není napevno v kódu) */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3 xl:col-span-2'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.nationalProxy')}</h2>
          <p className='text-xs text-gray-500'>{t('eduroam.nationalProxyDesc')}</p>
          <div className='space-y-2'>
            {form.national_proxy.servers.map((srv, i) => (
              <div key={i} className='flex gap-2 items-center'>
                <input
                  className={`${inputCls} flex-1`}
                  placeholder='flr1.eduroam.cz'
                  value={srv.host}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      national_proxy: {
                        ...f.national_proxy,
                        servers: f.national_proxy.servers.map((s, si) =>
                          si === i ? { ...s, host: e.target.value } : s
                        ),
                      },
                    }))}
                />
                <input
                  className={`${inputCls} flex-1 font-mono`}
                  placeholder='78.128.248.10'
                  value={srv.ip}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      national_proxy: {
                        ...f.national_proxy,
                        servers: f.national_proxy.servers.map((s, si) =>
                          si === i ? { ...s, ip: e.target.value } : s
                        ),
                      },
                    }))}
                />
                <button
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      national_proxy: {
                        ...f.national_proxy,
                        servers: f.national_proxy.servers.filter((_, si) => si !== i),
                      },
                    }))}
                  className='p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg shrink-0'
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  national_proxy: {
                    ...f.national_proxy,
                    servers: [...f.national_proxy.servers, { host: '', ip: '' }],
                  },
                }))}
              className={secondaryBtn}
            >
              <Plus size={13} /> {t('eduroam.addServer')}
            </button>
          </div>
          <div className='grid grid-cols-2 gap-3 pt-2'>
            <Field label='Port (RadSec)'>
              <input
                className={inputCls}
                type='number'
                value={form.national_proxy.port}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    national_proxy: { ...f.national_proxy, port: Number(e.target.value) },
                  }))}
              />
            </Field>
            <Field label={t('eduroam.sharedSecret')}>
              <input
                className={inputCls}
                type='password'
                value={form.national_proxy.secret}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    national_proxy: { ...f.national_proxy, secret: e.target.value },
                  }))}
              />
            </Field>
          </div>
        </div>

        {/* Monitoring probe federace — samostatný stroj mimo FLR pool */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.monitoringTitle')}</h2>
          <p className='text-xs text-gray-500'>{t('eduroam.monitoringDesc')}</p>
          <div className='grid grid-cols-2 gap-3'>
            <Field label='Host'>
              <input
                className={inputCls}
                value={form.monitoring.host}
                onChange={(e) =>
                  setForm((f) => ({ ...f, monitoring: { ...f.monitoring, host: e.target.value } }))}
              />
            </Field>
            <Field label='IP'>
              <input
                className={`${inputCls} font-mono`}
                value={form.monitoring.ip}
                onChange={(e) =>
                  setForm((f) => ({ ...f, monitoring: { ...f.monitoring, ip: e.target.value } }))}
              />
            </Field>
          </div>
          <Field label={t('eduroam.sharedSecret')}>
            <input
              className={inputCls}
              type='password'
              value={form.monitoring.secret}
              onChange={(e) =>
                setForm((f) => ({ ...f, monitoring: { ...f.monitoring, secret: e.target.value } }))}
            />
          </Field>
        </div>

        {/* Test user */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.testAccount')}</h2>
          <p className='text-xs text-gray-500'>
            {t('eduroam.testAccountDesc')}
          </p>
          <label className='flex items-center gap-2 text-sm text-gray-300'>
            <input
              type='checkbox'
              checked={form.test_user.enabled}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  test_user: { ...f.test_user, enabled: e.target.checked },
                }))}
              className='rounded'
            />
            {t('eduroam.active')}
          </label>
          <div className='grid grid-cols-2 gap-3'>
            <Field label='Username'>
              <input
                className={inputCls}
                value={form.test_user.username}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    test_user: { ...f.test_user, username: e.target.value },
                  }))}
              />
            </Field>
            <Field label={t('eduroam.password')}>
              <input
                className={inputCls}
                type='password'
                value={form.test_user.password}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    test_user: { ...f.test_user, password: e.target.value },
                  }))}
              />
            </Field>
          </div>
        </div>

        {/* Misc */}
        <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.misc')}</h2>
          <Field label='Operator-Name (RFC 5580)'>
            <input
              className={inputCls}
              value={form.operator_name}
              onChange={(e) => setForm((f) => ({ ...f, operator_name: e.target.value }))}
              placeholder='1:school.example'
            />
          </Field>
          <Field label='Cert (server.crt)'>
            <input
              className={inputCls}
              value={form.certificate.cert_file}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  certificate: { ...f.certificate, cert_file: e.target.value },
                }))}
            />
          </Field>
          <Field label='Key (server.key)'>
            <input
              className={inputCls}
              value={form.certificate.key_file}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  certificate: { ...f.certificate, key_file: e.target.value },
                }))}
            />
          </Field>
          <Field label='CA (ca.crt)'>
            <input
              className={inputCls}
              value={form.certificate.ca_file}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  certificate: { ...f.certificate, ca_file: e.target.value },
                }))}
            />
          </Field>
        </div>

        <div className='col-span-full'>
          <button type='submit' disabled={saveMut.isPending} className={primaryBtn}>
            {saveMut.isPending ? t('eduroam.savingDots') : t('eduroam.saveConfig')}
          </button>
        </div>
      </form>

      {/* Dry-run test */}
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-3'>
        <h2 className='text-white font-semibold text-sm'>{t('eduroam.dryRunTitle')}</h2>
        <div className='grid grid-cols-2 gap-3'>
          <Field label='Username'>
            <input
              className={inputCls}
              value={testUser}
              onChange={(e) => setTestUser(e.target.value)}
            />
          </Field>
          <Field label='Realm'>
            <input
              className={inputCls}
              value={testRealm}
              onChange={(e) => setTestRealm(e.target.value)}
              placeholder={form.realms[0]?.name ?? 'school.example'}
            />
          </Field>
        </div>
        <button
          onClick={() => testMut.mutate({ username: testUser, realm: testRealm || undefined })}
          disabled={testMut.isPending}
          className={secondaryBtn}
        >
          {testMut.isPending ? t('eduroam.testing') : t('eduroam.runDryRun')}
        </button>
        {testMut.data && (
          <pre className='text-xs text-gray-400 bg-gray-950/50 rounded-xl p-3 overflow-auto'>
            {JSON.stringify(testMut.data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ── Certs tab ─────────────────────────────────────────────────────────────────

// FreeRADIUS potřebuje tyto soubory — cesty jsou napevno v proxy.conf
const REQUIRED_CERTS = [
  {
    name: 'server.crt',
    label: 'eduroam.certServerLabel',
    required: true,
    desc: 'eduroam.certServerDesc',
    warning: 'eduroam.certServerWarning',
    howto: 'eduroam.certServerHowto',
  },
  {
    name: 'server.key',
    label: 'eduroam.certKeyLabel',
    required: true,
    desc: 'eduroam.certKeyDesc',
    warning: 'eduroam.certKeyWarning',
    howto: 'eduroam.certKeyHowto',
  },
  {
    name: 'edupki-ca.crt',
    label: 'eduroam.certEdupkiLabel',
    required: true,
    desc: 'eduroam.certEdupkiDesc',
    warning: 'eduroam.certEdupkiWarning',
    howto: 'eduroam.certEdupkiHowto',
  },
  {
    name: 'eap-ca.crt',
    label: 'eduroam.certEapLabel',
    required: false,
    desc: 'eduroam.certEapDesc',
    warning: '',
    howto: 'eduroam.certEapHowto',
  },
];

function CertsTab({ status, refreshAll }: { status?: EduroamStatus; refreshAll: () => void }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState<string | null>(null);
  const [contents, setContents] = useState<Record<string, string>>({});

  const uploaded = new Set((status?.certificates ?? []).map((c) => c.name));

  const saveMut = useMutation({
    mutationFn: ({ name, content }: { name: string; content: string }) =>
      api.mcp('sso.eduroam_cert', { action: 'save', name, content }),
    onSuccess: (_r, { name }) => {
      toast.success(t('eduroam.certSaved', { name }));
      setContents((c) => ({ ...c, [name]: '' }));
      setUploading(null);
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (name: string) => api.mcp('sso.eduroam_cert', { action: 'delete', name }),
    onSuccess: () => {
      toast.success(t('eduroam.certDeleted'));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-2'>
        <div className='flex items-center gap-2 mb-3'>
          <FileCode2 size={15} className='text-blue-400' />
          <h2 className='text-white font-semibold text-sm'>{t('eduroam.tlsCertsTitle')}</h2>
        </div>
        <p className='text-xs text-gray-500 mb-4'>
          {t('eduroam.certsPathDesc1')}{' '}
          <code className='text-gray-300'>/var/acm/modules/eduroam/certs/</code>{' '}
          {t('eduroam.certsPathDesc2')}{' '}
          <code className='text-gray-300'>/etc/freeradius/certs/eduroam/</code>{t('eduroam.certsPathDesc3')}
        </p>
        {REQUIRED_CERTS.map((cert) => {
          const isUploaded = uploaded.has(cert.name);
          const isOpen = uploading === cert.name;
          const content = contents[cert.name] ?? '';
          const borderCls = isUploaded
            ? 'border-emerald-500/20 bg-emerald-500/5'
            : cert.required
            ? 'border-gray-800 bg-gray-950/40'
            : 'border-gray-800/50 bg-gray-950/20';
          return (
            <div key={cert.name} className={`rounded-xl border ${borderCls} p-4 space-y-2`}>
              <div className='flex items-center justify-between gap-3'>
                <div className='flex items-center gap-2 min-w-0'>
                  {isUploaded
                    ? <CheckCircle size={14} className='text-emerald-400 flex-shrink-0' />
                    : cert.required
                    ? <XCircle size={14} className='text-gray-600 flex-shrink-0' />
                    : (
                      <div className='w-3.5 h-3.5 rounded-full border border-gray-700 flex-shrink-0' />
                    )}
                  <div className='min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-white font-mono'>{cert.name}</span>
                      {!cert.required && (
                        <span className='text-[10px] text-gray-600 border border-gray-700 rounded px-1'>
                          {t('eduroam.optionalBadge')}
                        </span>
                      )}
                    </div>
                    <div className='text-xs text-gray-500 mt-0.5'>{t(cert.label)} — {t(cert.desc)}</div>
                  </div>
                </div>
                <div className='flex gap-2 shrink-0'>
                  {isUploaded && (
                    <button
                      onClick={() => delMut.mutate(cert.name)}
                      className='p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-red-500/10'
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                  <button
                    onClick={() => setUploading(isOpen ? null : cert.name)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg ${
                      isOpen
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    {isUploaded ? t('eduroam.replace') : t('eduroam.upload')}
                  </button>
                </div>
              </div>
              {/* Warning + howto */}
              {!isUploaded && (cert.warning || cert.howto) && (
                <div className='text-xs space-y-1 pl-6'>
                  {cert.warning && <div className='text-amber-400/80'>{t(cert.warning)}</div>}
                  {cert.howto && <div className='text-gray-500'>{t(cert.howto)}</div>}
                </div>
              )}
              {isOpen && (
                <div className='space-y-2 pt-1'>
                  <textarea
                    className={`${inputCls} min-h-[120px] font-mono text-xs`}
                    value={content}
                    onChange={(e) => setContents((c) => ({ ...c, [cert.name]: e.target.value }))}
                    placeholder='-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'
                    autoFocus
                  />
                  <button
                    onClick={() => saveMut.mutate({ name: cert.name, content })}
                    disabled={saveMut.isPending || !content.trim()}
                    className={primaryBtn}
                  >
                    {saveMut.isPending ? t('eduroam.savingDots') : t('eduroam.saveCert', { name: cert.name })}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Status */}
      <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-4 text-sm'>
        <div className='text-xs text-gray-500 mb-2'>{t('eduroam.certStatus')}</div>
        {REQUIRED_CERTS.filter((c) => c.required).every((c) => uploaded.has(c.name))
          ? (
            <div className='text-emerald-400 text-sm'>
              {t('eduroam.requiredUploaded')}
            </div>
          )
          : (
            <div className='text-amber-400 text-sm'>
              {t('eduroam.missingRequired')}{' '}
              {REQUIRED_CERTS.filter((c) => c.required && !uploaded.has(c.name)).map((c) => c.name)
                .join(', ')}
            </div>
          )}
      </div>
    </div>
  );
}

// ── VLAN tab ──────────────────────────────────────────────────────────────────

function VlanTab(
  { configQ, refreshAll }: { configQ: { data?: EduroamConfig }; refreshAll: () => void },
) {
  const { t } = useTranslation();
  const [newRule, setNewRule] = useState({ match: 'realm', value: '', vlan: 200, label: '' });

  const addMut = useMutation({
    mutationFn: () => api.mcp('sso.eduroam_vlan_rules', { action: 'add', ...newRule }),
    onSuccess: () => {
      toast.success(t('eduroam.ruleAdded'));
      setNewRule({ match: 'realm', value: '', vlan: 200, label: '' });
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.mcp('sso.eduroam_vlan_rules', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('eduroam.ruleDeleted'));
      refreshAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rules = configQ.data?.vlan_rules ?? [];

  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5 space-y-4'>
      <div className='flex items-center gap-2'>
        <Plus size={15} className='text-amber-400' />
        <h2 className='text-white font-semibold text-sm'>{t('eduroam.vlanRules')}</h2>
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <Field label='Match'>
          <Select
            value={newRule.match}
            onChange={(e) => setNewRule((r) => ({ ...r, match: e.target.value }))}
          >
            <option value='realm'>realm</option>
            <option value='group'>group</option>
          </Select>
        </Field>
        <Field label={t('eduroam.value')}>
          <input
            className={inputCls}
            value={newRule.value}
            onChange={(e) => setNewRule((r) => ({ ...r, value: e.target.value }))}
          />
        </Field>
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <Field label='VLAN ID'>
          <input
            className={inputCls}
            type='number'
            value={newRule.vlan}
            onChange={(e) => setNewRule((r) => ({ ...r, vlan: Number(e.target.value) }))}
          />
        </Field>
        <Field label='Label'>
          <input
            className={inputCls}
            value={newRule.label}
            onChange={(e) => setNewRule((r) => ({ ...r, label: e.target.value }))}
          />
        </Field>
      </div>
      <button
        onClick={() => addMut.mutate()}
        disabled={addMut.isPending || !newRule.value}
        className={primaryBtn}
      >
        {addMut.isPending ? t('eduroam.adding') : t('eduroam.addRule')}
      </button>
      <div className='space-y-2 pt-2'>
        {rules.map((rule) => (
          <div
            key={rule.id}
            className='flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950/50 px-3 py-2'
          >
            <div>
              <div className='text-sm text-white'>{rule.label || rule.id}</div>
              <div className='text-xs text-gray-500'>
                {rule.match}={rule.value} → VLAN {rule.vlan}
              </div>
            </div>
            <button
              onClick={() => delMut.mutate(rule.id)}
              className='p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10'
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {rules.length === 0 && (
          <div className='text-center py-6 text-gray-600 text-sm'>{t('eduroam.noVlanRules')}</div>
        )}
      </div>
    </div>
  );
}

// ── Events tab ────────────────────────────────────────────────────────────────

function EventsTab({ events }: { events: EduroamEvent[] }) {
  const { t } = useTranslation();
  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-5'>
      <h2 className='text-white font-semibold text-sm mb-4'>{t('eduroam.moduleEvents')}</h2>
      <div className='space-y-2'>
        {events.slice(0, 30).map((ev, i) => (
          <div
            key={i}
            className='rounded-xl border border-gray-800 bg-gray-950/50 px-3 py-2'
          >
            <div className='flex items-center justify-between gap-3'>
              <div className='text-sm text-white'>{ev.type}</div>
              <div className='text-xs text-gray-500'>
                {new Date(ev.created_at).toLocaleString('cs-CZ')}
              </div>
            </div>
            {ev.detail && (
              <pre className='text-xs text-gray-500 mt-1.5 overflow-auto'>
                {JSON.stringify(ev.detail, null, 2)}
              </pre>
            )}
          </div>
        ))}
        {events.length === 0 && (
          <div className='text-center py-8 text-gray-600 text-sm'>{t('eduroam.noEvents')}</div>
        )}
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function ValidateConfigButton() {
  const { t } = useTranslation();
  const [result, setResult] = useState<{ ok: boolean; output: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const validate = async () => {
    setLoading(true);
    try {
      const res = await api.mcp<{ ok: boolean; output: string }>('sso.eduroam_validate', {});
      setResult(res);
    } catch (e) {
      setResult({ ok: false, output: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <button onClick={validate} disabled={loading} className={secondaryBtn}>
        <ShieldCheck size={13} /> {loading ? t('eduroam.validating') : 'Validate config'}
      </button>
      {result && (
        <div
          className={`rounded-xl border px-3 py-2 text-xs font-mono max-h-40 overflow-auto ${
            result.ok
              ? 'border-emerald-500/20 bg-emerald-500/5 text-emerald-300'
              : 'border-red-500/20 bg-red-500/5 text-red-300'
          }`}
        >
          {result.output || (result.ok ? t('eduroam.configValid') : t('eduroam.validationError'))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className='block'>
      <div className='text-xs text-gray-500 mb-1.5'>{label}</div>
      {children}
    </label>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <div className='text-xs text-gray-500'>{label}</div>
      <div className='text-xs text-gray-300 font-mono truncate'>{value}</div>
    </>
  );
}

function Stat(
  { label, value, tone = 'blue' }: {
    label: string;
    value: string;
    tone?: 'blue' | 'green' | 'red';
  },
) {
  const toneCls = tone === 'green'
    ? 'text-emerald-400'
    : tone === 'red'
    ? 'text-red-400'
    : 'text-blue-400';
  return (
    <div className='rounded-2xl border border-gray-800 bg-gray-900/30 p-4'>
      <div className='text-xs text-gray-500 mb-1'>{label}</div>
      <div className={`text-lg font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

const inputCls =
  'w-full rounded-xl border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const primaryBtn =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50 transition-colors';
const secondaryBtn =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-700 disabled:opacity-50 transition-colors';
